// Unleash flag URL-override (dev / test tooling — BOO-203)
// -----------------------------------------------------------------------------
// Lets a developer or tester FORCE an Unleash flag on/off (or to a specific
// variant) from the URL, overriding whatever the Unleash server actually
// returns — so a flag can be exercised in local / sandbox / staging WITHOUT
// toggling it in the Unleash UI.
//
// USAGE
//   ?unleashOverride=<flag>:<value>[,<flag>:<value>...]
//     value = on | off | <variantName>
//       on   -> { enabled: true,  variant: { name: 'enabled',  ... } }
//       off  -> { enabled: false, variant: { name: 'disabled', ... } }
//       any  -> { enabled: true,  variant: { name: <value>, ... } }  (force a named variant)
//   Examples:
//     ?unleashOverride=AHA-28900-autocomplete:on
//     ?unleashOverride=release-feature-upgrade-modal:feature-upgrade-new-ui
//     ?unleashOverride=AHA-28900-autocomplete:on,release-scim:off
//   Clearing (`clear` and `reset` are interchangeable, case-insensitive):
//     ?unleashOverride=clear                 -> clear ALL overrides
//     ?unleashOverride=reset                 -> same as `clear`
//     ?unleashOverride=<flag>:clear          -> clear ONE flag
//     ?unleashOverride=<flag>:reset          -> same as `<flag>:clear`
//     window.clearUnleashOverride()          -> clear ALL from the console
//
// WHEN THE PARAM IS READ: exactly once per page load, on the first
// getUnleashOverride() call — the resolved entries are then memoized for the
// life of the module singleton. So adding/changing ?unleashOverride= during a
// CLIENT-SIDE route change (no full reload) is a no-op until you refresh the
// page. Change the param, then reload.
//
// SCOPE: the override lives in sessionStorage, so it is SESSION-scoped, not
// sticky. It survives SPA navigation (after the query param drops off the URL)
// and full page reloads within the same tab — but it is gone as soon as the tab
// is closed, so a later visit starts clean and never silently retains a forced
// flag. Use a fresh tab (or the clear options above) to drop it sooner.
//
// ENV GATE: an ALLOWLIST — `Config.env` (VUE_APP_ENVAPP) must be one of
// OVERRIDE_ALLOWED_ENVS below ('local' | 'develop' | 'production'), covering
// local dev, every develop-flavoured build (sandbox / staging /
// developEU / developPPTAddin / dev.buildGoogleSlide) AND production (production,
// canary and their EU / PPT-addin / GoogleSlide variants). Any env not listed —
// 'slideReadOnly', and any env value that does not exist yet — is ignored, so a
// NEW env file still defaults to SAFE and has to be added here deliberately.
// ('slideReadOnly' is omitted because it is inert there anyway: store-read-only
// stubs the whole featureToggles module and never touches UnleashService.)
//
// PRODUCTION IS DELIBERATELY INCLUDED (BOO-203, product owner's call). The
// trade-off was raised explicitly and accepted: AhaSlides Unleash flags ENABLE
// features rather than guard free users — plan/entitlement gating is done via
// pricing + plan checks, not these flags — so forcing one on prod turns a
// feature on for yourself, it does not unlock a paid plan. Being able to
// exercise a flag on real production data was judged worth that risk.
//   Known caveats if you are re-evaluating this later:
//   * The override can force a flag OFF as well as on. A flag that ENABLES a
//     restriction is therefore the risk direction — e.g.
//     `release-AHA-41396-team-settings-paywall` (canUseTeamSettingsPaywall)
//     turns a paywall ON, so forcing it off removes that paywall client-side.
//   * This is client-side only. It cannot bypass anything the backend enforces;
//     it only changes what this browser renders, for the person who set it.
//
// COVERAGE — consciously left uncovered:
//   * The two raw `unleashClient.getVariant` marketplace pre-fetch call sites in
//     featureToggles.module.js read the SDK directly, before the store exists,
//     and are not overridable.
//   * Anything reading `state.features` directly (there are no such call sites
//     outside the module today — the getters are the chokepoint).
//
// Flag names contain hyphens but never a colon, so the FIRST ':' in each
// comma-separated segment cleanly splits <flag> from <value>.
import Config from '@/config'
import STORAGE_KEYS from '@/constant/storageKeys'

const STORAGE_KEY = STORAGE_KEYS.UNLEASH_OVERRIDE
const QUERY_PARAM = 'unleashOverride'

// The accepted spellings for each value (case-insensitive). Kept minimal: these
// are the only tokens the usage docs advertise, so the surface stays small and
// any other value is unambiguously treated as a forced variant name. Clearing
// accepts two synonyms — both `clear` and `reset` are documented above.
const OFF_VALUES = new Set(['off'])
const ON_VALUES = new Set(['on'])
const CLEAR_VALUES = new Set(['clear', 'reset'])

// ENV GATE: allowlist — see the header block. 'production' is included on
// purpose (BOO-203): these flags enable features, they don't guard free users.
// Anything not listed ('slideReadOnly', plus any env introduced later) stays off
// by default.
const OVERRIDE_ALLOWED_ENVS = new Set(['local', 'develop', 'production'])

const isOverrideAllowedEnv = () => OVERRIDE_ALLOWED_ENVS.has(Config.env)
const hasWindow = () => typeof window !== 'undefined'

// In-memory cache of the resolved override entries ({ [flag]: entry }). Built
// once on first read; the module singleton keeps it across SPA navigation.
let _entries = null

// sessionStorage (NOT localStorage) is deliberate: it keeps the override alive
// across navigation + reloads in this tab, then drops it when the tab closes so
// the override can never quietly persist into a future visit.
const readStore = () => {
  if (!hasWindow()) return {}
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : {}
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch (error) {
    return {}
  }
}

const writeStore = (map) => {
  if (!hasWindow()) return
  try {
    if (map && Object.keys(map).length) {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map))
    } else {
      window.sessionStorage.removeItem(STORAGE_KEY)
    }
  } catch (error) {
    // sessionStorage may be unavailable (private mode, blocked) — silently ignore.
  }
}

// Parse "flagA:on,flagB:variantX" into { flagA: 'on', flagB: 'variantX' }.
export const parseOverrideString = (raw) => {
  const map = {}
  if (!raw || typeof raw !== 'string') return map
  raw.split(',').forEach((segment) => {
    const pair = segment.trim()
    if (!pair) return
    const idx = pair.indexOf(':')
    if (idx === -1) return
    const flag = pair.slice(0, idx).trim()
    const value = pair.slice(idx + 1).trim()
    if (flag && value) map[flag] = value
  })
  return map
}

// Build a synthetic feature entry matching the shape the Vuex getters read:
// { enabled, variant: { name, enabled, feature_enabled, payload } }.
const makeEntry = (name, enabled) => ({
  enabled,
  variant: {
    name, enabled, feature_enabled: enabled, payload: null,
  },
})

// Turn an override VALUE string into a synthetic feature entry.
//   'off' -> disabled, 'on' -> enabled, anything else -> that named variant
//   (enabled). The original case is preserved for named variants so checks like
//   `variant.name === 'feature-upgrade-new-ui'` match.
export const valueToEntry = (value) => {
  const normalized = String(value).toLowerCase()
  if (OFF_VALUES.has(normalized)) return makeEntry('disabled', false)
  if (ON_VALUES.has(normalized)) return makeEntry('enabled', true)
  return makeEntry(value, true)
}

// Read the query param once, merge into the persisted store, and return the
// resulting override map. Handles whole-map clear and per-flag clear.
// Only ever reached on an allowlisted env (getUnleashOverride short-circuits
// everywhere else).
const initFromQuery = () => {
  if (!hasWindow()) return {}
  let stored = readStore()

  try {
    const params = new URLSearchParams(window.location.search)
    if (params.has(QUERY_PARAM)) {
      const raw = (params.get(QUERY_PARAM) || '').trim()

      if (!raw || CLEAR_VALUES.has(raw.toLowerCase())) {
        // ?unleashOverride=clear  (or empty) -> wipe everything
        stored = {}
      } else {
        const parsed = parseOverrideString(raw)
        stored = { ...stored, ...parsed }
        // Support per-flag clear: `flag:clear` removes just that flag.
        Object.keys(parsed).forEach((flag) => {
          if (CLEAR_VALUES.has(String(parsed[flag]).toLowerCase())) {
            delete stored[flag]
          }
        })
      }
      writeStore(stored)
    }
  } catch (error) {
    // Malformed URL — fall back to whatever was already persisted.
  }

  return stored
}

// Resolve the raw override map to entries, once, and cache them. The URL is
// parsed a single time on first read, so every subsequent lookup is O(1) with
// no re-parsing or per-read allocation.
const getEntries = () => {
  if (_entries === null) {
    const raw = initFromQuery()
    // Null-prototype map so a flag literally named `toString`/`constructor`
    // can't resolve to an inherited method on the direct lookup below.
    _entries = Object.create(null)
    Object.keys(raw).forEach((flag) => { _entries[flag] = valueToEntry(raw[flag]) })
  }
  return _entries
}

/**
 * Resolve the forced override for a flag, if any.
 * @param {string} flagName
 * @returns {{ enabled: boolean, variant: object } | undefined}
 *   A synthetic feature entry when the flag is overridden, or `undefined` when
 *   there is no override (or on a non-allowlisted env, where overrides are
 *   disabled) — in which case the caller must fall through to the real Unleash
 *   value.
 *
 * This is the single env gate: off the allowlist it returns `undefined` before
 * any parsing/storage work, so an unlisted env can't force flags at all, and the
 * non-overridden read path costs only one Set lookup.
 */
export const getUnleashOverride = (flagName) => {
  if (!isOverrideAllowedEnv() || !flagName) return undefined
  return getEntries()[flagName]
}

/**
 * Merge the active overrides over a whole feature map.
 * @param {object} features - the real `state.features` map from Unleash.
 * @returns {object} `features` untouched when nothing is overridden (the
 *   common case — no allocation), otherwise a shallow copy with the synthetic
 *   override entries layered on top. Entries have the same
 *   `{ enabled, variant: { name, enabled, feature_enabled, payload } }` shape
 *   as real ones, so every consumer of the whole map (the zoid-embedded
 *   micro-frontends and the Mixpanel flag context) sees the forced values too,
 *   instead of the UI and the embeds/analytics disagreeing.
 */
export const applyUnleashOverrides = (features) => {
  if (!isOverrideAllowedEnv()) return features
  const entries = getEntries()
  const flags = Object.keys(entries)
  if (!flags.length) return features
  const merged = { ...features }
  flags.forEach((flag) => { merged[flag] = entries[flag] })
  return merged
}

/** Clear every override (memory + sessionStorage). */
export const clearUnleashOverrides = () => {
  _entries = Object.create(null)
  writeStore({})
}

// Expose a console helper on allowlisted envs so testers can clear overrides
// without hunting for the sessionStorage key (or just close the tab).
if (hasWindow() && isOverrideAllowedEnv()) {
  window.clearUnleashOverride = clearUnleashOverrides
}
